import React from 'react';
import Swal from 'sweetalert2';
import churchForms from 'src/services/api/churchForms';
import {
  useRecaptchaToken,
  isCaptchaRequired,
  RECAPTCHA_SECURITY_ERROR,
} from 'src/components/helpers/recaptchaUtils';
import {
  applyGuestToInputs,
  hasFormErrors,
  verifyCreateNetworkForm,
  verifyLookupFields,
  withPendingRevision,
} from 'src/containers/comunidad-network/form/networkFormUtils';
import NetworkFormDisclaimer from 'src/containers/comunidad-network/form/NetworkFormDisclaimer';
import NetworkFormSuccess from 'src/containers/comunidad-network/form/NetworkFormSuccess';
import NetworkFormReviewNotice from 'src/containers/comunidad-network/form/NetworkFormReviewNotice';
import NetworkFormLookupStep from 'src/containers/comunidad-network/form/NetworkFormLookupStep';
import { NetworkFormCreateOwner } from 'src/containers/comunidad-network/form/NetworkFormOwnerSection';
import { NetworkFormCreateActions } from 'src/containers/comunidad-network/form/NetworkFormActions';
import NetworkFormLayout from 'src/containers/comunidad-network/form/NetworkFormLayout';
import NetworkCompanyFormBody from 'src/containers/comunidad-network/form/NetworkCompanyFormBody';
import {
  submitNetworkCompany,
  useNetworkCompanyForm,
} from 'src/containers/comunidad-network/form/useNetworkCompanyForm';

const SENT_MESSAGES = {
  create:
    'Gracias, la información ha sido enviada con éxito. Pronto será revisada y procesada para su publicación.',
  update: 'Actualizamos tu inscripción. Pronto será revisada y procesada para su publicación.',
  revision:
    'Recibimos tus cambios. Tu empresa sigue visible en el directorio con la información actual hasta que un administrador apruebe la actualización.',
};

const reviewNotice = (network) => {
  if (!network.published) {
    return 'Ya tienes una inscripción en revisión. Puedes actualizarla antes de que sea publicada.';
  }
  if (network.hasPendingRevision) {
    return 'Ya enviaste cambios que están pendientes de revisión. Aquí ves esa propuesta; si la envías de nuevo, reemplaza la anterior. El directorio sigue mostrando la versión publicada hasta que un administrador la apruebe.';
  }
  return 'Tu empresa ya está publicada. Los cambios que envíes quedarán pendientes de revisión y el directorio seguirá mostrando la información actual hasta que un administrador los apruebe.';
};

const RedComunidadEmpresa = () => {
  const [sentMessage, setSentMessage] = React.useState(null);
  const [lookingUp, setLookingUp] = React.useState(false);
  const [step, setStep] = React.useState('lookup');
  const [existingNetwork, setExistingNetwork] = React.useState(null);
  const getLookupCaptchaToken = useRecaptchaToken('comunidad_network_lookup');
  const getSubmitCaptchaToken = useRecaptchaToken('comunidad_network');
  const captchaRequired = isCaptchaRequired();
  const {
    inputs,
    setInputs,
    errorInputs,
    setErrorInputs,
    sending,
    setSending,
    logoFile,
    logoPreview,
    catalogItems,
    clearMedia,
    handleInputs,
    selectHandle,
    setJobPostings,
    loadRecord,
    reset,
    media,
  } = useNetworkCompanyForm();

  const requestEligibleGuest = async (identification, phone) => {
    const captcha = await getLookupCaptchaToken({ skip: !captchaRequired });
    if (captchaRequired && !captcha) {
      Swal.fire(RECAPTCHA_SECURITY_ERROR);
      return { captchaFailed: true };
    }

    const response = await churchForms.checkComunidadNetworkEligible({
      identification: String(identification).trim(),
      phone: String(phone).trim(),
      ...(captchaRequired ? { captcha } : {}),
    });
    return { response };
  };

  const goToLookup = () => {
    setStep('lookup');
    setErrorInputs({});
    clearMedia();
    if (existingNetwork) {
      setInputs((prev) => ({ identification: prev.identification, phone: prev.phone }));
      setExistingNetwork(null);
    }
  };

  const lookupGuest = async (e) => {
    e?.preventDefault?.();
    const nextErrors = verifyLookupFields(inputs);
    setErrorInputs(nextErrors);
    if (hasFormErrors(nextErrors)) return;

    setLookingUp(true);
    try {
      const { captchaFailed, response } = await requestEligibleGuest(
        inputs.identification,
        inputs.phone
      );
      if (captchaFailed) return;

      const { guest, network } = response?.data || {};
      if (response?.data?.success && guest) {
        if (network) {
          loadRecord({ ...withPendingRevision(network), Guest: guest });
          setExistingNetwork({
            id: network.id,
            published: Boolean(network.published),
            hasPendingRevision: Boolean(network.pendingRevision),
          });
        } else {
          setInputs((prev) => applyGuestToInputs(guest, prev));
          setExistingNetwork(null);
        }
        setErrorInputs({});
        setStep('form');
      } else {
        Swal.fire(
          response?.data?.message || 'No fue posible continuar. Verifica tu cédula y celular.'
        );
      }
    } catch (error) {
      Swal.fire(error?.response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
    } finally {
      setLookingUp(false);
    }
  };

  const onGuestUpdated = async (updated) => {
    const identification = updated?.identification || inputs.identification;
    const phone = updated?.phone || inputs.phone;
    if (!identification || !phone) return;

    setLookingUp(true);
    try {
      const { captchaFailed, response } = await requestEligibleGuest(identification, phone);
      if (captchaFailed) return;

      if (response?.data?.success && response.data.guest) {
        setInputs((prev) => applyGuestToInputs(response.data.guest, prev));
      } else {
        Swal.fire(
          response?.data?.message ||
            'Actualizamos tus datos, pero no fue posible continuar. Verifica la cédula y el celular.'
        );
        setStep('lookup');
      }
    } catch (error) {
      Swal.fire(error?.response?.data?.message || 'Hubo un error al actualizar los datos.');
    } finally {
      setLookingUp(false);
    }
  };

  const sendForm = async (e) => {
    e?.preventDefault?.();
    const nextErrors = verifyCreateNetworkForm(inputs);
    setErrorInputs(nextErrors);
    if (hasFormErrors(nextErrors)) return;

    const kind = !existingNetwork ? 'create' : existingNetwork.published ? 'revision' : 'update';

    setSending(true);
    try {
      const { captchaFailed, response } = await submitNetworkCompany({
        captchaRequired,
        getCaptchaToken: getSubmitCaptchaToken,
        revision: kind === 'revision',
        formData: {
          inputs,
          logoFile,
          logoPreview,
          catalogItems,
          extraFields: { networkId: existingNetwork?.id },
        },
      });
      if (captchaFailed) {
        Swal.fire(RECAPTCHA_SECURITY_ERROR);
        return;
      }

      if (response?.data?.success) {
        Swal.fire('Enviado correctamente');
        reset();
        setExistingNetwork(null);
        setSentMessage(SENT_MESSAGES[kind]);
        setStep('lookup');
      } else if (response) {
        Swal.fire(response.data?.message || 'Hubo un error, por favor intenta más tarde.');
      }
    } catch (error) {
      Swal.fire(error?.response?.data?.message || 'Hubo un error, por favor intenta más tarde.');
    } finally {
      setSending(false);
    }
  };

  return (
    <NetworkFormLayout
      title="Directorio de Empresas, Vendedores y Servicios"
      subtitle="Completa el formulario para publicar o actualizar tu empresa o servicio en el directorio de Comunidad."
    >
      {sentMessage ? (
        <NetworkFormSuccess message={sentMessage} />
      ) : (
        <>
          <NetworkFormDisclaimer />
          {step === 'lookup' ? (
            <NetworkFormLookupStep
              inputs={inputs}
              errorInputs={errorInputs}
              lookingUp={lookingUp}
              handleInputs={handleInputs}
              onContinue={lookupGuest}
            />
          ) : (
            <>
              {existingNetwork && (
                <NetworkFormReviewNotice>{reviewNotice(existingNetwork)}</NetworkFormReviewNotice>
              )}
              <NetworkCompanyFormBody
                inputs={inputs}
                errorInputs={errorInputs}
                sending={sending}
                lookingUp={lookingUp}
                handleInputs={handleInputs}
                selectHandle={selectHandle}
                media={media}
                onJobPostingsChange={setJobPostings}
                owner={
                  <NetworkFormCreateOwner
                    inputs={inputs}
                    errorInputs={errorInputs}
                    lookingUp={lookingUp}
                    sending={sending}
                    onGuestUpdated={onGuestUpdated}
                    onChangeIdentity={goToLookup}
                  />
                }
                actions={
                  <NetworkFormCreateActions
                    errorInputs={errorInputs}
                    sending={sending}
                    lookingUp={lookingUp}
                    onSubmit={sendForm}
                  />
                }
              />
            </>
          )}
        </>
      )}
    </NetworkFormLayout>
  );
};

export default RedComunidadEmpresa;
